'use client'

import { useLocalStorage } from '@uidotdev/usehooks'
import { useRouter, useSearchParams } from 'next/navigation'
import { FC, useEffect } from 'react'
import redirectToBooking from '../api/redirect'
import {
	DEFAULT_PROVIDER,
	DEFAULT_TAB,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
} from '../constants'
import { BookingDataLocalStorage, HotelItemLocalStorage } from '../types'
import { addBookingFilterParam } from '../utils/kayakRedirect'
import { addTrackingIdToUrl } from '../utils/skyscannerRedirect'

export interface Props {
	hotelItem: HotelItemLocalStorage
	redirectInPlace?: boolean
	onRedirect?: () => void
	windowRef?: Window | null
}

const RedirectCaller: FC<Props> = ({ hotelItem, redirectInPlace, onRedirect, windowRef }: Props) => {
	const router = useRouter()
	const urlSearchParams = useSearchParams()

	const [bookingData, b] = useLocalStorage<BookingDataLocalStorage>(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)

	useEffect(() => {
		const goToLink = (url: string) => {
			if (redirectInPlace) {
				if(windowRef) windowRef.location.href = url
			} else {
				setTimeout(() => {
					router.replace(url)
				}, 1500)					
			}
		}
		const performRedirect = async () => {
			try {
				const res = await redirectToBooking({
					tab: DEFAULT_TAB,
					rooms: bookingData.rooms,
					adults: bookingData._adults,
					children: bookingData._children,
					los: bookingData.los,
					dta: bookingData.dta,
					city: bookingData['city'],
					currency: hotelItem.currency,
					price: hotelItem.price,
					lang: hotelItem.lang,
					stars: hotelItem.stars,
					'additional-price': hotelItem.additionalPrice,
					'hotel-name': hotelItem.rawHotelData.name,
					'hotel-country': bookingData['hotel-country'],
					'location-name': bookingData['location-name'],
					'check-in-date': hotelItem.searchParams['check-in-date'],
					'check-out-date': hotelItem.searchParams['check-out-date'],
					'redirect-provider': DEFAULT_PROVIDER,
					'redirect-partner': hotelItem.rawHotelData.bestRate.provider.name,
					// Pass partner and filter parameters to backend
					f: urlSearchParams.get('f') || undefined,
					b: urlSearchParams.get('b') || undefined,
					fuid: urlSearchParams.get('fuid') || undefined,
					hf: urlSearchParams.get('hf') || undefined,
				})
				let redirectLink = addTrackingIdToUrl(hotelItem.link, res?.trackingID)
				goToLink(redirectLink)
			} catch (error) {
				console.error('Error during redirect:', error)
				let fallbackLink = hotelItem.link

				// Add hf=3 parameter for Booking.com redirects even in error case
				const f = urlSearchParams.get('f')
				const b = urlSearchParams.get('b')
				fallbackLink = addBookingFilterParam(fallbackLink, f, b)
				goToLink(fallbackLink)
			} finally {
				onRedirect?.()
			}
		}

		if (hotelItem) {
			performRedirect()
		}
	}, [router, hotelItem, bookingData, urlSearchParams])

	return null
}

export default RedirectCaller
